import { useEffect, useState } from 'react';
import { Button, Dialog, Input, Label } from '../components/ui';
import {
    useCrewEmail,
    usePreviewCrewRoute,
    useSendCrewRoute,
    useSetCrewEmail,
    type RoutePreview,
} from '../lib/crewEmail';
import { formatDateShort } from '../lib/utils';

/**
 * Send one crew their list for one date.
 *
 * Milestone 9: "a user picks a crew and a date, sees a preview of exactly what
 * will go out, and sends."
 *
 * The preview is rendered by the Edge Function, not here. That is the whole
 * point of the word exactly. Building the email in the browser and the real one
 * on the server would be two renderers, and they would agree right up until
 * somebody changed one of them.
 *
 * The HTML lands in a sandboxed iframe rather than through
 * dangerouslySetInnerHTML. It is a full document with its own body styles, so
 * injecting it inline would leak those styles across the app, and sandbox=""
 * means nothing in it can execute even if a service item somehow carried a
 * script.
 */
export function SendRouteDialog({
    open,
    onClose,
    crew,
    isoDate,
}: {
    open: boolean;
    onClose: () => void;
    crew: { id: string; name: string } | null;
    isoDate: string;
}) {
    const preview = usePreviewCrewRoute();
    const send = useSendCrewRoute();
    const setEmail = useSetCrewEmail();
    const { data: crewEmail } = useCrewEmail(crew?.id);

    const [data, setData] = useState<RoutePreview | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [sentId, setSentId] = useState<string | null>(null);
    const [emailDraft, setEmailDraft] = useState('');

    // Re-preview whenever the dialog opens or the crew or date changes. The
    // list is fetched server side, so an item added since the board last
    // refreshed still appears in what actually goes out.
    useEffect(() => {
        if (!open || !crew) return;
        setData(null);
        setError(null);
        setSentId(null);
        preview
            .mutateAsync({ crewId: crew.id, isoDate })
            .then((res) => {
                setData(res);
                setEmailDraft(res.crew.email ?? '');
            })
            .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not build the preview'));
        // preview is a stable mutation object; including it re-fires forever.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, crew?.id, isoDate]);

    async function handleSaveEmail() {
        if (!crew) return;
        setError(null);
        try {
            await setEmail.mutateAsync({ crewId: crew.id, email: emailDraft });
            const res = await preview.mutateAsync({ crewId: crew.id, isoDate });
            setData(res);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not save that address');
        }
    }

    async function handleSend() {
        if (!crew) return;
        setError(null);
        try {
            const res = await send.mutateAsync({ crewId: crew.id, isoDate });
            setSentId(res.provider_message_id ?? 'sent');
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not send');
        }
    }

    const address = data?.crew.email ?? crewEmail ?? null;
    const hasAddress = Boolean(address);
    const priorSends = (data?.previous_sends ?? []).filter((s) => s.status === 'sent');
    const busy = preview.isPending || send.isPending || setEmail.isPending;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={crew ? `Email ${crew.name}` : 'Email crew'}
            subtitle={`Service list for ${formatDateShort(isoDate)}`}
            maxWidth="lg"
            footer={
                sentId ? (
                    <Button variant="primary" size="sm" onClick={onClose}>
                        Done
                    </Button>
                ) : (
                    <>
                        <Button variant="ghost" size="sm" onClick={onClose} disabled={send.isPending}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleSend}
                            disabled={busy || !hasAddress || !data || data.item_count === 0}
                        >
                            {send.isPending ? 'Sending...' : 'Send'}
                        </Button>
                    </>
                )
            }
        >
            {sentId ? (
                <p className="text-sm text-graphite-800 dark:text-graphite-200">
                    Sent to {address}. {data?.item_count}{' '}
                    {data?.item_count === 1 ? 'stop' : 'stops'} for {formatDateShort(isoDate)}.
                </p>
            ) : (
                <div className="flex flex-col gap-3">
                    {error ? (
                        <div className="px-3 py-2 rounded border border-red-500/40 bg-red-500/10 font-mono text-xs text-red-600 dark:text-red-400">
                            {error}
                        </div>
                    ) : null}

                    {/* No address means no send. The field is here rather than
                        buried in a settings screen, because this is the moment
                        somebody finds out it is missing. */}
                    {!hasAddress ? (
                        <div className="px-3 py-3 rounded border border-amber-500/40 bg-amber-500/10">
                            <Label htmlFor="crew-email">
                                {crew?.name} has no email address on file
                            </Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    id="crew-email"
                                    type="email"
                                    value={emailDraft}
                                    placeholder="crew@example.com"
                                    onChange={(e) => setEmailDraft(e.target.value)}
                                />
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleSaveEmail}
                                    disabled={!emailDraft.trim() || setEmail.isPending}
                                >
                                    {setEmail.isPending ? 'Saving...' : 'Save'}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="font-mono text-xs text-graphite-600 dark:text-graphite-400">
                            To {address}
                            {data ? (
                                <>
                                    {' · '}
                                    {data.item_count} {data.item_count === 1 ? 'stop' : 'stops'}
                                </>
                            ) : null}
                        </div>
                    )}

                    {/* Warn rather than block. Sara may legitimately resend
                        after adding a stop, and refusing would send her back to
                        doing it by hand. */}
                    {priorSends.length > 0 ? (
                        <div className="px-3 py-2 rounded border border-amber-500/40 bg-amber-500/10 text-xs text-graphite-800 dark:text-graphite-200">
                            Already sent {priorSends.length === 1 ? 'once' : `${priorSends.length} times`} for
                            this date. Last went to {priorSends[0].recipient} with{' '}
                            {priorSends[0].item_count} {priorSends[0].item_count === 1 ? 'stop' : 'stops'}.
                        </div>
                    ) : null}

                    {data && data.item_count === 0 ? (
                        <p className="text-sm text-graphite-500">
                            Nothing is scheduled for this crew on this date, so there is nothing to send.
                        </p>
                    ) : null}

                    {preview.isPending && !data ? (
                        <p className="font-mono text-xs text-graphite-500 uppercase tracking-widest">
                            Building the preview...
                        </p>
                    ) : null}

                    {data?.html ? (
                        <div className="border border-graphite-300 dark:border-graphite-700 rounded overflow-hidden">
                            <iframe
                                title="Email preview"
                                srcDoc={data.html}
                                sandbox=""
                                className="w-full h-[52vh] bg-white"
                            />
                        </div>
                    ) : null}
                </div>
            )}
        </Dialog>
    );
}
